/**
 * RoamerWalletModule.ts — Module #2: the Roamer Wallet™.
 *
 * A platform module (NOT a core engine, NOT a banking app): financial
 * intelligence and planning for roamers. Integration rules:
 *  • Registers with Roamer Core™ as an EngineAdapter via attachToCore().
 *  • Inbound data arrives ONLY as Core bus events — PaymentReceived (income),
 *    LocationChanged (city context for cost-of-living), CurrencyUpdated
 *    (refresh signal; the bus payload carries no rates, so none are invented)
 *    — plus manual entry and, later, provider integrations (interfaces in
 *    providers.ts; not implemented per the brief).
 *  • Outbound: notifications published on the bus as NotificationSent
 *    (goalReached / budgetExceeded) — never direct engine calls.
 *  • Money honesty: transactions keep their own currency; aggregates convert
 *    through RECORDED rates only. No known rate ⇒ excluded and counted in
 *    unconvertibleCount. Missing metrics are null, never zero.
 *  • Financial data is private by default; export / delete supported.
 *    Persistence at nomad:wallet:v1 with record-by-record sanitization.
 */

import { roamerCore, type CoreEvent, type EngineAdapter } from '@/services/core';
import { storageManager } from '@/services/storage/StorageManager';
import {
  DEFAULT_WALLET_PRIVACY,
  GOAL_TYPES,
  SPENDING_CATEGORIES,
  type BudgetSnapshot,
  type ExchangeRateEntry,
  type GoalType,
  type PeriodSummary,
  type SpendingCategory,
  type TrendPoint,
  type WalletExport,
  type WalletGoal,
  type WalletInsight,
  type WalletPrivacy,
  type WalletProfile,
  type WalletReports,
  type WalletTransaction,
} from './models';
import {
  averageMonthly,
  cityCosts,
  computeBudgetSnapshot,
  monthStart,
  sumBetween,
  yearStart,
  type HomeAmount,
} from './budget';
import { generateInsights } from './insights';

const STORAGE_KEY = 'nomad:wallet:v1';
const MODULE_ID = 'roamer-wallet-module';
const DAY = 24 * 60 * 60 * 1000;
const MAX_TRANSACTIONS = 2000;
const MAX_RATES = 500;

interface WalletStay {
  city: string;
  arrivedAt: number;
  departedAt: number | null;
}

interface PersistedState {
  settings: WalletSettings;
  transactions: WalletTransaction[];
  rates: ExchangeRateEntry[];
  goals: WalletGoal[];
  stays: WalletStay[];
  privacy: WalletPrivacy;
  notified: { budgetExceededMonth: number | null };
}

/** User-editable profile settings (derived fields live in getProfile()). */
export interface WalletSettings {
  homeCurrency: string;
  currentCurrency: string;
  preferredCurrencies: string[];
  monthlyIncomeGoal: number | null;
  monthlySpendingGoal: number | null;
  savingsGoal: number | null;
  emergencyFundGoal: number | null;
}

const DEFAULT_SETTINGS: WalletSettings = {
  homeCurrency: 'USD',
  currentCurrency: 'USD',
  preferredCurrencies: [],
  monthlyIncomeGoal: null,
  monthlySpendingGoal: null,
  savingsGoal: null,
  emergencyFundGoal: null,
};

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

function asPositiveNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

export class RoamerWalletModule {
  private settings: WalletSettings = { ...DEFAULT_SETTINGS };
  private transactions: WalletTransaction[] = [];
  private rates: ExchangeRateEntry[] = [];
  private goals: WalletGoal[] = [];
  private stays: WalletStay[] = [];
  private privacy: WalletPrivacy = { ...DEFAULT_WALLET_PRIVACY };
  private notified: { budgetExceededMonth: number | null } = { budgetExceededMonth: null };
  private unsubscribes: (() => void)[] = [];
  private core: typeof roamerCore | null = null;
  /** Bumped on Career domain bus events (refresh signals — Career™ owns the data). */
  private careerRefreshSignals = 0;
  private listeners = new Set<() => void>();

  constructor(now: number = Date.now()) {
    this.load(now);
  }

  // ── Core integration (the ONLY communication channel) ──────────────────────

  buildAdapter(): EngineAdapter {
    return {
      id: MODULE_ID,
      name: 'Roamer Wallet™',
      version: '1.0.0',
      capabilities: ['wallet', 'budgeting', 'multi-currency', 'financial-insights', 'goals'],
      healthCheck: async () => true,
    };
  }

  attachToCore(core: typeof roamerCore = roamerCore, now: number = Date.now()): void {
    this.detachFromCore();
    core.registerEngine(this.buildAdapter(), now);
    const sub = (name: Parameters<typeof core.on>[0], handler: (e: CoreEvent) => void) => {
      this.unsubscribes.push(core.on(name, (e) => {
        try {
          handler(e);
        } catch {
          /* a bad payload must never break the bus */
        }
      }, 'module:wallet'));
    };
    sub('PaymentReceived', (e) => this.onPaymentReceived(e));
    sub('LocationChanged', (e) => this.onLocationChanged(e));
    sub('CurrencyUpdated', () => { /* refresh signal only — the bus payload carries no rates */ });
    // ECOSYSTEM SYNCHRONIZATION™ (Build 2.04.0): Career domain events are
    // refresh signals ONLY. Career™ owns income records — Wallet™ never
    // copies them (no duplicated ownership); subscribers re-read Career via
    // its own module. The counter lets diagnostics/UI prove liveness.
    sub('IncomeRecorded', () => { this.careerRefreshSignals += 1; });
    sub('IncomeUpdated', () => { this.careerRefreshSignals += 1; });
    sub('CareerUpdated', () => { this.careerRefreshSignals += 1; });
    this.core = core;
  }

  detachFromCore(): void {
    for (const u of this.unsubscribes) u();
    this.unsubscribes = [];
    this.core = null;
  }

  /** Read-only: whether the module is currently attached to Roamer Core. */
  /** How many Career domain refresh signals arrived (liveness diagnostics). */
  getCareerRefreshSignalCount(): number {
    return this.careerRefreshSignals;
  }

  isAttached(): boolean {
    return this.core != null;
  }

  // ── Event handlers (defensive parsing — never fabricate) ───────────────────

  private onPaymentReceived(e: CoreEvent): void {
    const p = (e.payload ?? {}) as Record<string, unknown>;
    const amount = asPositiveNumber(p.amount);
    if (amount == null) return;
    this.pushTransaction({
      kind: 'income',
      amount,
      currency: asString(p.currency) ?? this.settings.currentCurrency,
      category: null,
      description: asString(p.description) ?? asString(p.title) ?? 'Payment received',
      city: this.currentCity(),
      at: e.at,
      source: 'bus',
    }, e.at);
  }

  private onLocationChanged(e: CoreEvent): void {
    const raw = (e.payload ?? {}) as Record<string, unknown>;
    // Real Core traffic wraps the location: { location: RoamerLocation }.
    const p = (raw.location != null && typeof raw.location === 'object'
      ? raw.location
      : raw) as Record<string, unknown>;
    const city = asString(p.city) ?? asString(p.name);
    if (!city) return;
    const open = this.stays.find((s) => s.departedAt == null);
    if (open && open.city === city) return;
    if (open) open.departedAt = e.at;
    this.stays.push({ city, arrivedAt: e.at, departedAt: null });
    this.afterUpdate(e.at);
  }

  private currentCity(): string | null {
    return this.stays.find((s) => s.departedAt == null)?.city ?? null;
  }

  // ── Manual entry APIs ───────────────────────────────────────────────────────

  recordExpense(
    input: { amount: number; currency?: string; category: SpendingCategory; description?: string; city?: string | null },
    at: number = Date.now(),
  ): WalletTransaction | null {
    if (!(input.amount > 0) || !SPENDING_CATEGORIES.includes(input.category)) return null;
    return this.pushTransaction({
      kind: 'expense',
      amount: input.amount,
      currency: input.currency ?? this.settings.currentCurrency,
      category: input.category,
      description: input.description ?? '',
      city: input.city !== undefined ? input.city : this.currentCity(),
      at,
      source: 'manual',
    }, at);
  }

  recordIncome(
    input: { amount: number; currency?: string; description?: string },
    at: number = Date.now(),
  ): WalletTransaction | null {
    if (!(input.amount > 0)) return null;
    return this.pushTransaction({
      kind: 'income',
      amount: input.amount,
      currency: input.currency ?? this.settings.currentCurrency,
      category: null,
      description: input.description ?? '',
      city: this.currentCity(),
      at,
      source: 'manual',
    }, at);
  }

  private pushTransaction(tx: Omit<WalletTransaction, 'id'>, at: number): WalletTransaction {
    const full: WalletTransaction = { ...tx, id: `tx-${at}-${this.transactions.length}` };
    this.transactions.push(full);
    if (this.transactions.length > MAX_TRANSACTIONS) {
      this.transactions = this.transactions.slice(-MAX_TRANSACTIONS);
    }
    this.afterUpdate(at);
    return structuredClone(full);
  }

  /** Record an exchange rate (manual today; providers later). 1 from = rate to. */
  setExchangeRate(from: string, to: string, rate: number, at: number = Date.now()): boolean {
    if (!(rate > 0) || !from || !to || from === to) return false;
    this.rates.push({ from, to, rate, at, source: 'manual' });
    if (this.rates.length > MAX_RATES) this.rates = this.rates.slice(-MAX_RATES);
    this.afterUpdate(at);
    return true;
  }

  updateSettings(patch: Partial<WalletSettings>, at: number = Date.now()): WalletSettings {
    this.settings = {
      ...this.settings,
      ...patch,
      preferredCurrencies: (patch.preferredCurrencies ?? this.settings.preferredCurrencies)
        .filter((c) => typeof c === 'string'),
    };
    this.afterUpdate(at);
    return { ...this.settings };
  }

  // ── Currency conversion (recorded rates only — never guessed) ──────────────

  /** Latest known rate from → to, using direct or inverse entries. Null if unknown. */
  getRate(from: string, to: string): number | null {
    if (from === to) return 1;
    const direct = [...this.rates].reverse().find((r) => r.from === from && r.to === to);
    if (direct) return direct.rate;
    const inverse = [...this.rates].reverse().find((r) => r.from === to && r.to === from);
    return inverse ? 1 / inverse.rate : null;
  }

  convert(amount: number, from: string, to: string): number | null {
    const rate = this.getRate(from, to);
    return rate != null ? amount * rate : null;
  }

  /** Resolve transactions into home currency; unresolvable ones are counted, not guessed. */
  private homeAmounts(): { txs: HomeAmount[]; unconvertible: number } {
    const home = this.settings.homeCurrency;
    const txs: HomeAmount[] = [];
    let unconvertible = 0;
    for (const t of this.transactions) {
      const converted = this.convert(t.amount, t.currency, home);
      if (converted == null) {
        unconvertible += 1;
        continue;
      }
      txs.push({ kind: t.kind, amount: converted, category: t.category, city: t.city, at: t.at });
    }
    return { txs, unconvertible };
  }

  // ── Goals ───────────────────────────────────────────────────────────────────

  addGoal(type: GoalType, title: string, targetAmount: number, at: number = Date.now()): WalletGoal | null {
    if (!GOAL_TYPES.includes(type) || !(targetAmount > 0)) return null;
    const goal: WalletGoal = {
      id: `goal-${type}-${at}`,
      type,
      title,
      targetAmount,
      currentAmount: 0,
      createdAt: at,
      reachedAt: null,
    };
    this.goals.push(goal);
    this.afterUpdate(at);
    return structuredClone(this.goals[this.goals.length - 1]);
  }

  /** Manual contribution for goal types the bus cannot derive (travel/housing). */
  contributeToGoal(goalId: string, amount: number, at: number = Date.now()): boolean {
    const goal = this.goals.find((g) => g.id === goalId);
    if (!goal || !(amount > 0)) return false;
    goal.currentAmount += amount;
    this.afterUpdate(at);
    return true;
  }

  getGoals(now: number = Date.now()): WalletGoal[] {
    this.refreshGoalProgress(now);
    return structuredClone(this.goals);
  }

  /** Auto-track derivable goals from real transactions. */
  private refreshGoalProgress(now: number): void {
    const { txs } = this.homeAmounts();
    const mStart = monthStart(now);
    const incomeThisMonth = sumBetween(txs, 'income', mStart, now + 1);
    const missionIncome = this.transactions
      .filter((t) => t.kind === 'income' && t.source === 'bus')
      .reduce((s, t) => s + (this.convert(t.amount, t.currency, this.settings.homeCurrency) ?? 0), 0);
    const lifetimeNet =
      txs.filter((t) => t.kind === 'income').reduce((s, t) => s + t.amount, 0) -
      txs.filter((t) => t.kind === 'expense').reduce((s, t) => s + t.amount, 0);
    for (const goal of this.goals) {
      if (goal.type === 'income') goal.currentAmount = incomeThisMonth;
      else if (goal.type === 'missionIncome') goal.currentAmount = missionIncome;
      else if (goal.type === 'savings' || goal.type === 'emergencyFund') {
        goal.currentAmount = Math.max(0, lifetimeNet);
      }
      // travel / housing accumulate via contributeToGoal only.
    }
  }

  private checkGoalNotifications(at: number): void {
    for (const goal of this.goals) {
      if (goal.reachedAt == null && goal.currentAmount >= goal.targetAmount) {
        goal.reachedAt = at;
        this.publishNotification('goalReached', { goalId: goal.id, title: goal.title }, at);
      }
    }
  }

  private checkBudgetNotifications(snapshot: BudgetSnapshot, at: number): void {
    const month = monthStart(at);
    if (snapshot.health === 'over-budget' && this.notified.budgetExceededMonth !== month) {
      this.notified.budgetExceededMonth = month;
      this.publishNotification('budgetExceeded', { monthlyBudget: snapshot.monthlyBudget }, at);
    }
  }

  private publishNotification(kind: string, payload: Record<string, unknown>, at: number): void {
    try {
      this.core?.publish('NotificationSent', MODULE_ID, { kind, ...payload }, at);
    } catch {
      /* announcements are best-effort */
    }
  }

  // ── Reads (deep-cloned, honest nulls) ───────────────────────────────────────

  getProfile(now: number = Date.now()): WalletProfile {
    const { txs, unconvertible } = this.homeAmounts();
    const snapshot = computeBudgetSnapshot(txs, this.baseProfile(), unconvertible, now);
    return structuredClone({
      ...this.baseProfile(),
      averageMonthlyIncome: averageMonthly(txs, 'income', now),
      averageMonthlyExpenses: averageMonthly(txs, 'expense', now),
      currentExchangeRate: this.getRate(this.settings.homeCurrency, this.settings.currentCurrency),
      budgetHealth: snapshot.health,
    });
  }

  private baseProfile(): WalletProfile {
    return {
      homeCurrency: this.settings.homeCurrency,
      currentCurrency: this.settings.currentCurrency,
      preferredCurrencies: [...this.settings.preferredCurrencies],
      monthlyIncomeGoal: this.settings.monthlyIncomeGoal,
      monthlySpendingGoal: this.settings.monthlySpendingGoal,
      savingsGoal: this.settings.savingsGoal,
      emergencyFundGoal: this.settings.emergencyFundGoal,
      averageMonthlyIncome: null,
      averageMonthlyExpenses: null,
      currentExchangeRate: null,
      budgetHealth: 'unknown',
    };
  }

  getBudget(now: number = Date.now()): BudgetSnapshot {
    const { txs, unconvertible } = this.homeAmounts();
    return computeBudgetSnapshot(txs, this.baseProfile(), unconvertible, now);
  }

  getInsights(now: number = Date.now()): WalletInsight[] {
    const { txs, unconvertible } = this.homeAmounts();
    const snapshot = computeBudgetSnapshot(txs, this.baseProfile(), unconvertible, now);
    return generateInsights({
      txs,
      snapshot,
      rates: this.rates,
      homeCurrency: this.settings.homeCurrency,
      currentCurrency: this.settings.currentCurrency,
      stays: this.stays,
      currentCity: this.currentCity(),
      now,
    });
  }

  getTransactions(): WalletTransaction[] {
    return structuredClone(this.transactions);
  }

  getRateHistory(from: string, to: string): ExchangeRateEntry[] {
    return structuredClone(this.rates.filter((r) => r.from === from && r.to === to));
  }

  getReports(now: number = Date.now()): WalletReports {
    const { txs, unconvertible } = this.homeAmounts();
    const home = this.settings.homeCurrency;
    const summary = (from: number, to: number, label: string): PeriodSummary | null => {
      const inPeriod = txs.filter((t) => t.at >= from && t.at < to);
      if (inPeriod.length === 0) return null;
      const byCategory: Partial<Record<SpendingCategory, number>> = {};
      for (const t of inPeriod) {
        if (t.kind === 'expense' && t.category != null) {
          byCategory[t.category] = (byCategory[t.category] ?? 0) + t.amount;
        }
      }
      const income = inPeriod.filter((t) => t.kind === 'income').reduce((s, t) => s + t.amount, 0);
      const expenses = inPeriod.filter((t) => t.kind === 'expense').reduce((s, t) => s + t.amount, 0);
      return {
        label,
        currency: home,
        income,
        expenses,
        net: income - expenses,
        byCategory,
        transactionCount: inPeriod.length,
        unconvertibleCount: unconvertible,
      };
    };

    const trend = (kind: 'income' | 'expense'): TrendPoint[] => {
      const points: TrendPoint[] = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now);
        const from = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i, 1);
        const to = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - i + 1, 1);
        points.push({
          label: new Date(from).toISOString().slice(0, 7),
          value: sumBetween(txs, kind, from, to),
        });
      }
      return points;
    };

    const savingsGrowth: TrendPoint[] = [];
    let running = 0;
    for (const point of trend('income').map((p, i) => ({
      label: p.label,
      value: p.value - trend('expense')[i].value,
    }))) {
      running += point.value;
      savingsGrowth.push({ label: point.label, value: running });
    }

    return structuredClone({
      monthlySummary: summary(monthStart(now), now + 1, new Date(now).toISOString().slice(0, 7)),
      yearlySummary: summary(yearStart(now), now + 1, String(new Date(now).getUTCFullYear())),
      citySpendingComparison: cityCosts(txs, this.stays, now),
      incomeTrend: trend('income'),
      expenseTrend: trend('expense'),
      currencyTrend: this.rates
        .filter((r) => r.from === home && r.to === this.settings.currentCurrency)
        .map((r) => ({ label: new Date(r.at).toISOString().slice(0, 10), value: r.rate })),
      savingsGrowth,
    });
  }

  // ── Privacy, export, deletion ───────────────────────────────────────────────

  getPrivacy(): WalletPrivacy {
    return { ...this.privacy };
  }

  updatePrivacy(patch: Partial<WalletPrivacy>): WalletPrivacy {
    this.privacy = { ...this.privacy, ...patch };
    this.persist();
    return this.getPrivacy();
  }

  /** Shared view — financial data is private by default; flags gate each slice. */
  getSharedView(now: number = Date.now()): { summary: PeriodSummary | null; goals: WalletGoal[] | null } | null {
    if (!this.privacy.shareSummaries && !this.privacy.shareGoals) return null;
    return structuredClone({
      summary: this.privacy.shareSummaries ? this.getReports(now).monthlySummary : null,
      goals: this.privacy.shareGoals ? this.getGoals(now) : null,
    });
  }

  /** Full portable export (also serves as backup). */
  exportWallet(now: number = Date.now()): WalletExport {
    return structuredClone({
      exportedAt: now,
      profile: this.getProfile(now),
      transactions: this.transactions,
      rates: this.rates,
      goals: this.goals,
      privacy: this.privacy,
    });
  }

  /** Delete all financial history. */
  deleteAll(at: number = Date.now()): void {
    this.settings = { ...DEFAULT_SETTINGS };
    this.transactions = [];
    this.rates = [];
    this.goals = [];
    this.stays = [];
    this.privacy = { ...DEFAULT_WALLET_PRIVACY };
    this.notified = { budgetExceededMonth: null };
    this.persist();
    this.emitChange();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitChange(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch { /* isolated */ }
    }
  }

  private afterUpdate(at: number): void {
    this.refreshGoalProgress(at);
    this.checkGoalNotifications(at);
    const { txs, unconvertible } = this.homeAmounts();
    this.checkBudgetNotifications(computeBudgetSnapshot(txs, this.baseProfile(), unconvertible, at), at);
    this.persist();
    this.emitChange();
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = {
        settings: this.settings,
        transactions: this.transactions,
        rates: this.rates,
        goals: this.goals,
        stays: this.stays,
        privacy: this.privacy,
        notified: this.notified,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      storageManager.push('wallet', 'state', state);
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  /** Called by hydrateWalletFromCloud when a newer cloud copy is available. */
  reloadFromStorage(now = Date.now()): void {
    this.load(now);
    this.emitChange();
  }

  private load(now: number): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown> | null;
      if (state == null || typeof state !== 'object') return;

      const s = (state.settings ?? {}) as Record<string, unknown>;
      this.settings = {
        homeCurrency: asString(s.homeCurrency) ?? DEFAULT_SETTINGS.homeCurrency,
        currentCurrency: asString(s.currentCurrency) ?? DEFAULT_SETTINGS.currentCurrency,
        preferredCurrencies: Array.isArray(s.preferredCurrencies)
          ? s.preferredCurrencies.filter((c): c is string => typeof c === 'string')
          : [],
        monthlyIncomeGoal: asPositiveNumber(s.monthlyIncomeGoal),
        monthlySpendingGoal: asPositiveNumber(s.monthlySpendingGoal),
        savingsGoal: asPositiveNumber(s.savingsGoal),
        emergencyFundGoal: asPositiveNumber(s.emergencyFundGoal),
      };

      this.transactions = (Array.isArray(state.transactions) ? state.transactions : [])
        .filter((t): t is WalletTransaction =>
          t != null && typeof t === 'object' &&
          typeof t.id === 'string' &&
          (t.kind === 'income' || t.kind === 'expense') &&
          typeof t.amount === 'number' && t.amount > 0 &&
          typeof t.currency === 'string' &&
          typeof t.at === 'number' &&
          (t.category == null || SPENDING_CATEGORIES.includes(t.category)))
        .slice(-MAX_TRANSACTIONS);

      this.rates = (Array.isArray(state.rates) ? state.rates : [])
        .filter((r): r is ExchangeRateEntry =>
          r != null && typeof r === 'object' &&
          typeof r.from === 'string' && typeof r.to === 'string' &&
          typeof r.rate === 'number' && r.rate > 0 && typeof r.at === 'number')
        .slice(-MAX_RATES);

      this.goals = (Array.isArray(state.goals) ? state.goals : [])
        .filter((g): g is WalletGoal =>
          g != null && typeof g === 'object' &&
          typeof g.id === 'string' && GOAL_TYPES.includes(g.type) &&
          typeof g.targetAmount === 'number' && g.targetAmount > 0 &&
          typeof g.currentAmount === 'number');

      this.stays = (Array.isArray(state.stays) ? state.stays : [])
        .filter((st): st is WalletStay =>
          st != null && typeof st === 'object' &&
          typeof st.city === 'string' && typeof st.arrivedAt === 'number');

      const priv = (state.privacy ?? {}) as Record<string, unknown>;
      this.privacy = {
        shareSummaries: priv.shareSummaries === true,
        shareGoals: priv.shareGoals === true,
      };
      const notified = (state.notified ?? {}) as Record<string, unknown>;
      this.notified = {
        budgetExceededMonth: typeof notified.budgetExceededMonth === 'number'
          ? notified.budgetExceededMonth
          : null,
      };
    } catch {
      /* corrupt state — start fresh rather than crash */
    }
  }
}

/** App-wide singleton. Call attachToCore() during app bootstrap (after roamerCore.initialize()). */
export const roamerWalletModule = new RoamerWalletModule();

/**
 * Boot hydration: fetches the cloud copy of the Wallet™ state if it is
 * strictly newer than the last local write, writes it to localStorage, and
 * signals the module to reload.  Fire-and-forget — boot never blocks the network.
 */
export async function hydrateWalletFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<unknown>('wallet', 'state');
  if (!cloud) return false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('nomad:wallet:v1', JSON.stringify(cloud));
    }
  } catch { return false; }
  roamerWalletModule.reloadFromStorage();
  return true;
}
